import { TechnologyUseCreateDto, TechnologyUseSelectDto, TechnologyUseWithAssessmentsDto } from '@@types/api.types';
import { useAuth0 } from '@auth0/auth0-react';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook';
import { useGetCurrentUser } from '@queries/users.query';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from './client';
import { queryKeys } from './keys';

const invalidateTechnologyQueries = (technologyId: string) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.technology.getTechnology(technologyId) });
  queryClient.invalidateQueries({ queryKey: queryKeys.technology.getTechnologies });
  queryClient.invalidateQueries({ queryKey: queryKeys.technology.getTechnologyUsesWithAssessments(technologyId) });
  queryClient.invalidateQueries({ queryKey: queryKeys.technology.getTechnologyTrlAssessment(technologyId) });

  queryClient.refetchQueries({ queryKey: queryKeys.technology.getTechnologyUsesWithAssessments(technologyId) });
};

export const useCreateTechnologyUse = () => {
  const { apiPost } = useApi();
  const { isInnovator } = useGetCurrentUser();

  const onSuccess = (data: TechnologyUseSelectDto) => {
    invalidateTechnologyQueries(data.technologyId);
  };

  return useMutation<TechnologyUseSelectDto, ApiErrorWrapper, TechnologyUseCreateDto>({
    mutationFn: async (data) => {
      if (!isInnovator) {
        throw new Error('Only innovators can create technology uses');
      }
      return await apiPost<TechnologyUseSelectDto>({
        path: '/v1/innovator/technology-uses',
        body: data,
      });
    },
    onSuccess,
  });
};

export const useEditTechnologyUse = () => {
  const { apiPatch } = useApi();
  const { isInnovator } = useGetCurrentUser();

  const onSuccess = (_: TechnologyUseSelectDto, variables: TechnologyUseCreateDto & { id: string }) => {
    invalidateTechnologyQueries(variables.technologyId);
  };

  return useMutation({
    mutationFn: async (data: TechnologyUseCreateDto & { id: string }) => {
      if (!isInnovator) {
        throw new Error('Only innovators can update technology uses');
      }
      return await apiPatch<TechnologyUseSelectDto>({
        path: `/v1/innovator/technology-uses/${data.id}`,
        body: data,
      });
    },
    onSuccess,
  });
};

export const useGetTechnologyUsesWithAssessments = (technologyId?: string) => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const { isExpert, isIntel, isInnovator } = useGetCurrentUser();
  const enabled = isAuthenticated && technologyId !== undefined;

  return useQuery<TechnologyUseWithAssessmentsDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const path = isInnovator
        ? '/v1/innovator/technology-uses'
        : isExpert
          ? '/v1/expert/technology-uses'
          : isIntel
            ? '/v1/intel/technology-uses'
            : undefined;

      if (!path) {
        throw new Error('User role not supported for technology uses access');
      }

      const result = await apiGet<{ uses: TechnologyUseWithAssessmentsDto[] }>({
        path,
        query: { technologyId },
      });
      return result.uses;
    },
    queryKey:
      enabled && technologyId
        ? queryKeys.technology.getTechnologyUsesWithAssessments(technologyId)
        : queryKeys.placeholders.default,
  });
};

export const useDeleteTechnologyUse = () => {
  const { apiDelete } = useApi();
  const { isInnovator } = useGetCurrentUser();

  const onSuccess = (_: unknown, variables: { technologyId: string; technologyUseId: string }) => {
    invalidateTechnologyQueries(variables.technologyId);
  };

  return useMutation({
    mutationFn: async ({ technologyId, technologyUseId }: { technologyId: string; technologyUseId: string }) => {
      if (!isInnovator) {
        throw new Error('Only innovators can delete technology uses');
      }
      return await apiDelete({
        body: { technologyId },
        path: `/v1/innovator/technology-uses/${technologyUseId}`,
      });
    },
    onSuccess,
  });
};
